import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useLocation } from "@/context/LocationContext";
import CustomerLayout from "@/components/CustomerLayout";
import StoreCard from "@/components/StoreCard";
import ProductCard from "@/components/ProductCard";
import { SkeletonCard } from "@/components/States";
import { HeroSlider } from "@/components/HeroSlider";
import LocationModal from "@/components/LocationModal";
import CatalogCategoryRows from "@/components/CatalogCategoryRows";
import MembershipCard from "@/components/MembershipCard";
import { useSeo, orgJsonLd } from "@/lib/seo";
import { SectionHeader, CategoryNav, HowItWorks, WhyGrofkart, LocalStory,
  RetailerPartnerCTA, StartStoreBanner, DownloadApp, Testimonials, FAQ, SiteFooter } from "@/components/HomeSections";

const ProductRow = ({ title, subtitle, feed }) => {
  const nav = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ["feed", feed],
    queryFn: () => api.get("/products/feed", { params: { type: feed, limit: 12 } }).then((r) => r.data),
  });
  if (!isLoading && (!data || !data.length)) return null;
  return (
    <section className="mb-12" data-testid={`${feed}-products-section`}>
      <SectionHeader title={title} subtitle={subtitle} onSeeAll={() => nav("/stores")} />
      <div className="-mx-1 flex gap-3 overflow-x-auto px-1 pb-2 no-scrollbar">
        {isLoading
          ? Array.from({ length: 6 }).map((_, i) => <div key={i} className="w-40 shrink-0 sm:w-44"><SkeletonCard /></div>)
          : data.map((p) => (
              <div key={p.id} className="w-40 shrink-0 sm:w-44">
                <ProductCard product={p} store={{ id: p.store_id, name: p.store_name }} />
              </div>
            ))}
      </div>
    </section>
  );
};

const StoreRow = ({ title, subtitle, stores, isLoading, sort }) => {
  const nav = useNavigate();
  const list = sort === "rating" ? [...(stores || [])].sort((a, b) => (b.rating || 0) - (a.rating || 0)) : stores;
  if (!isLoading && (!list || !list.length)) return null;
  return (
    <section className="mb-12">
      <SectionHeader title={title} subtitle={subtitle} onSeeAll={() => nav("/stores")} />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {isLoading ? Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} />)
          : list.slice(0, 8).map((s) => <StoreCard key={s.id} store={s} />)}
      </div>
    </section>
  );
};

export default function Home() {
  const { location } = useLocation();
  const loc = { lat: location.lat, lng: location.lng };
  const [locOpen, setLocOpen] = useState(false);

  useSeo({ title: "Your Local Store, Now Online", path: "/", jsonLd: orgJsonLd(), jsonLdId: "gf-org" });

  const { data: cats } = useQuery({ queryKey: ["cats"], queryFn: () => api.get("/categories").then((r) => r.data) });
  const { data: stores, isLoading } = useQuery({ queryKey: ["stores", loc], queryFn: () => api.get("/stores", { params: loc }).then((r) => r.data) });
  const { data: testimonials } = useQuery({ queryKey: ["testimonials"], queryFn: () => api.get("/testimonials").then((r) => r.data) });
  const { data: faqs } = useQuery({ queryKey: ["faqs"], queryFn: () => api.get("/faqs").then((r) => r.data) });

  return (
    <CustomerLayout noFooter>
      <HeroSlider onOpenLocation={() => setLocOpen(true)} />
      <CategoryNav cats={cats} />
      <StoreRow title="Shops near you" subtitle="Your neighbourhood stores, now just a tap away." stores={stores} isLoading={isLoading} />
      <ProductRow feed="deals" title="Deals & Offers" subtitle="Today's best local savings" />
      <ProductRow feed="trending" title="Trending products" subtitle="Fast-moving neighbourhood favourites" />
      <MembershipCard variant="banner" />
      <StoreRow title="Popular stores" subtitle="Top-rated shops customers love" stores={stores} isLoading={isLoading} sort="rating" />
      <ProductRow feed="new" title="New arrivals" subtitle="Fresh on the shelves near you" />
      <CatalogCategoryRows />
      <LocalStory />
      <HowItWorks />
      <WhyGrofkart />
      <RetailerPartnerCTA />
      <StartStoreBanner />
      <DownloadApp />
      <Testimonials items={testimonials} />
      <FAQ items={faqs} />
      <div className="-mx-4 sm:-mx-6 lg:-mx-8"><SiteFooter /></div>
      <LocationModal open={locOpen} onClose={() => setLocOpen(false)} />
    </CustomerLayout>
  );
}
